import { cn } from '@/lib/cn';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slot } from '@radix-ui/react-slot';
import Tooltip from 'components/tooltip/Tooltip';
import React, { ButtonHTMLAttributes } from 'react';

interface BaseIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'aria-label'> {
    buttonClassName?: string;
}

/**
 * If tooltip title is a react element, aria-label must be provided to ensure accessibility.
 */
type IconButtonTitleProps =
    | {
          /** Tooltip title text */
          title: string;
          'aria-label'?: string;
      }
    | {
          /** Tooltip title text */
          title: React.ReactNode;
          'aria-label': string;
      };

type IconButtonProps = BaseIconButtonProps & IconButtonTitleProps;

export const IconButtonIcon = ({ icon, className }: { icon: IconProp; className?: string }) => (
    <FontAwesomeIcon icon={icon} className={cn('size-[16px]', className)} />
);

const IconButtonWrapper = ({
    className,
    buttonClassName,
    children,
    asChild,
    title,
    'aria-label': ariaLabel,
    ...props
}: IconButtonProps & {
    asChild?: boolean;
}) => {
    const Comp = asChild ? Slot : 'button';
    return (
        <Tooltip title={title} className={className}>
            <Comp
                className={cn(
                    'cursor-pointer size-[24px] items-center justify-center flex text-textPrimary hover:bg-componentTileBlockHover  focus-visible:bg-componentTileBlockHover rounded-md',
                    buttonClassName
                )}
                aria-label={typeof title === 'string' ? title : ariaLabel}
                {...props}
            >
                {children}
            </Comp>
        </Tooltip>
    );
};

/**
 * If `asChild` is true, the `icon` prop is not used and `children` must be provided.
 * This allows user to change to a link or other component, but must add <IconButton.Icon> as a child.
 */
type IconButtonConditionalProps =
    | {
          asChild: true;
          icon?: never;
          children: React.ReactNode;
      }
    | {
          asChild?: false;
          icon: IconProp;
          children?: React.ReactNode;
      };

/**
 * Render a simple button with just an icon and tooltip.
 *
 * @example
 * <IconButton icon={faPlus} title="Add" aria-label="Add" />
 *
 * @example
 * <IconButton asChild title="Add datasource">
 *   <Link to="/add">
 *     <IconButton.Icon icon={faPlus} />
 *   </Link>
 * </IconButton>
 */
const IconButton = ({ icon, asChild = false, children, ...props }: IconButtonProps & IconButtonConditionalProps) => (
    <IconButtonWrapper {...props}>{asChild ? children : <IconButtonIcon icon={icon!} />}</IconButtonWrapper>
);

export default Object.assign(IconButton, {
    Icon: IconButtonIcon
});
